import _ from 'lodash'
import {
	BaseDataItem,
	BaseDataItemWithMetadata,
	ConnectionType,
	ControllerConnectionItem,
	Data,
	pointers,
	QueryProperty,
	RepeaterDataItem,
	Structure,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
	TPAWidgetDataItem,
	ResolvedRefArray,
	ResolvedPresetsRefArray,
} from '@wix/thunderbolt-becky-types'
import { ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import { InflatedByRefComp, InflatedByRefStructure, RefData, ResolvedRef } from '../types/byRef'
import { DATA_QUERIES_TO_INFLATE, getEmptyDataMaps, getUniqueId, removeHash } from './utils'
import { shouldInflateRefArray } from './presetsUtils'
import { getUniqueDataQuery } from './byRefFunctionLibrary'
import { inflateWithPresets } from './presetsInflation'
import { inflateWithoutPresets, StyleOrLayoutItem, StyleOrLayoutMap } from './inflationUtils'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME
const MAP_NAME_TO_QUERY = pointers.MAP_NAME_TO_QUERY
const REPEATER_ITEM_DELIMITER = '__'

const getRepeaterAncestorIfExists = (id: string | null, structure: Structure): string | null => {
	if (!id || !structure[id]) {
		return null
	}

	if (structure[id].componentType === 'Repeater') {
		return id
	}

	return getRepeaterAncestorIfExists(structure[id].parent, structure)
}

const fixInflatedConnectionItem = (
	connectionItem: ControllerConnectionItem,
	originalStructure: Structure,
	refId: string
) => {
	if (connectionItem.type !== ConnectionType.ConnectionItem || !connectionItem.controllerId) {
		return connectionItem
	}

	const originalComp = Object.values(originalStructure).find(
		(value) => value.dataQuery === connectionItem.controllerId
	)

	if (!originalComp) {
		return connectionItem
	}

	const oldCompUniqueId = getUniqueId(refId, originalComp.id)
	const inflatedId = getUniqueDataQuery(oldCompUniqueId, PROPERTY_TYPES.DATA_QUERY)

	return { ...connectionItem, controllerId: inflatedId }
}

const isTPAWidgetDataItem = (dataItem: any): dataItem is TPAWidgetDataItem => dataItem.type === 'TPAWidget'

const inflateSingleDataItem = (
	originalDataItem: BaseDataItem<string>,
	dataQueryName: QueryProperty,
	comp: InflatedByRefComp,
	resolvedStructure: Structure,
	refId: string,
	clientSpecMap: ViewerClientSpecMap
) => {
	const inflatedDataQuery = comp[dataQueryName]
	const inflatedDataItem = { ...originalDataItem, id: inflatedDataQuery }
	if (dataQueryName === 'connectionQuery' && inflatedDataItem.type === 'ConnectionList') {
		// @ts-ignore
		inflatedDataItem.items = inflatedDataItem.items.map((connectionItem) =>
			fixInflatedConnectionItem(connectionItem, resolvedStructure, refId)
		)
	}
	if (dataQueryName === PROPERTY_TYPES.DATA_QUERY && isTPAWidgetDataItem(inflatedDataItem)) {
		const app = _.find(clientSpecMap, { appDefinitionId: inflatedDataItem.appDefinitionId })
		inflatedDataItem.applicationId = app?.applicationId || ''
	}

	return inflatedDataItem
}

const inflateRepeaterItems = (
	repeaterAncestorId: string,
	dataMapName: 'design_data' | 'document_data',
	dataQueryName: 'designQuery' | 'dataQuery',
	comp: InflatedByRefComp,
	originalInflatedDataItemId: string,
	remoteAndMasterPageStructure: Structure | InflatedByRefStructure,
	masterPageData: Data | RefData,
	resolvedData: Data | RefData,
	pageCompId: string
): { [id: string]: BaseDataItemWithMetadata } => {
	const { dataQuery: repeaterDataQuery } = remoteAndMasterPageStructure[repeaterAncestorId]

	const repeaterDataItem =
		repeaterDataQuery &&
		((masterPageData[DATA_MAPS.DATA][repeaterDataQuery] ||
			resolvedData[DATA_MAPS.DATA][repeaterDataQuery]) as RepeaterDataItem)

	if (!repeaterDataItem) {
		return {}
	}

	const { items } = repeaterDataItem

	return items.reduce((acc: { [id: string]: BaseDataItemWithMetadata }, item: string) => {
		const originalDataItemId = `${
			remoteAndMasterPageStructure[comp.templateId][dataQueryName]
		}${REPEATER_ITEM_DELIMITER}${item}`
		const originalItemData = (masterPageData[dataMapName][originalDataItemId] ||
			resolvedData[dataMapName][originalDataItemId]) as BaseDataItemWithMetadata

		if (originalItemData) {
			const inflatedDataItemId = `${originalInflatedDataItemId}${REPEATER_ITEM_DELIMITER}${item}`
			acc[inflatedDataItemId] = {
				...originalItemData,
				id: inflatedDataItemId,
				metaData: { ...originalItemData.metaData, pageId: pageCompId },
			} as BaseDataItemWithMetadata
		}
		return acc
	}, {})
}

const getOriginalDataItem = (
	dataQueryName: typeof DATA_QUERIES_TO_INFLATE[number], // TODO TS
	comp: InflatedByRefComp,
	resolvedStructure: InflatedByRefStructure,
	resolvedData: Data | RefData
) => {
	const inflatedDataQuery = comp[dataQueryName]
	if (!inflatedDataQuery) {
		return
	}
	const originalCompId = comp.templateId
	const originalDataQuery = resolvedStructure[originalCompId][dataQueryName]
	if (!originalDataQuery) {
		return
	}
	const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
	return resolvedData[dataMapName][originalDataQuery]
}

const inflateDataForRepeater = ({
	comp,
	dataMapName,
	dataQueryName,
	inflatedDataItem,
	masterPageData,
	resolvedData,
	pageCompId,
	inflatedDataAcc,
	getRemoteAndMasterPageStructure,
}: {
	comp: InflatedByRefComp
	dataMapName: 'design_data' | 'document_data'
	dataQueryName: 'dataQuery' | 'designQuery'
	inflatedDataItem: BaseDataItem<string>
	masterPageData: Data | RefData
	resolvedData: Data | RefData
	pageCompId: string
	inflatedDataAcc: RefData
	getRemoteAndMasterPageStructure: () => Structure
}) => {
	const remoteAndMasterPageStructure = getRemoteAndMasterPageStructure()
	const repeaterAncestorId = getRepeaterAncestorIfExists(comp.templateId, remoteAndMasterPageStructure)
	if (!repeaterAncestorId) {
		return
	}
	_.assign(
		inflatedDataAcc[dataMapName],
		inflateRepeaterItems(
			repeaterAncestorId,
			dataMapName,
			dataQueryName,
			comp,
			inflatedDataItem.id,
			remoteAndMasterPageStructure,
			masterPageData,
			resolvedData,
			pageCompId
		)
	)
}

const resolveRefArray = (refArray: UnresolvedRefArray, dataMap: StyleOrLayoutMap) =>
	({
		...refArray,
		values: refArray.values
			.map((value) => dataMap[removeHash(value)])
			.map((value) =>
				value.type === 'VariantRelation'
					? { ...value, to: dataMap[removeHash((value as UnresolvedVariantRelation).to)] }
					: value
			),
	} as ResolvedRefArray<StyleOrLayoutItem>)

const inflateRefArray = ({
	inflatedDataAcc,
	mapName,
	refArray,
	dataQuery,
	presets,
	mergedData,
}: {
	refArray: UnresolvedRefArray
	dataQuery: string
	mapName: 'layout_data' | 'theme_data'
	inflatedDataAcc: RefData
	presets: Array<ResolvedPresetsRefArray['values']>
	mergedData: Data
}) => {
	const resolvedRefArray = resolveRefArray(refArray, mergedData[mapName])

	if (presets.flat().length) {
		Object.assign(
			inflatedDataAcc[mapName],
			inflateWithPresets(resolvedRefArray, dataQuery, presets, mapName, mergedData[mapName])
		)
	} else {
		Object.assign(inflatedDataAcc[mapName], inflateWithoutPresets(resolvedRefArray, dataQuery, mergedData[mapName]))
	}
}

function inflateSingle({
	originalDataItem,
	refId,
	dataMapName,
	resolvedData,
	dataQueryName,
	getRemoteAndMasterPageStructure,
	inflatedDataAcc,
	pageCompId,
	masterPageData,
	comp,
	resolvedStructure,
	clientSpecMap,
}: {
	originalDataItem: BaseDataItem<string>
	dataQueryName: typeof DATA_QUERIES_TO_INFLATE[number] // TODO: TS
	comp: InflatedByRefComp
	resolvedStructure: InflatedByRefStructure
	refId: string
	inflatedDataAcc: RefData
	dataMapName: keyof Data
	masterPageData: Data | RefData
	resolvedData: Data | RefData
	pageCompId: string
	getRemoteAndMasterPageStructure: () => Structure
	clientSpecMap: ViewerClientSpecMap
}) {
	const inflatedDataItem = inflateSingleDataItem(
		originalDataItem,
		dataQueryName,
		comp,
		resolvedStructure,
		refId,
		clientSpecMap
	)
	inflatedDataAcc[dataMapName][inflatedDataItem.id] = inflatedDataItem

	if (dataQueryName === 'dataQuery' || dataQueryName === 'designQuery') {
		inflateDataForRepeater({
			comp,
			dataMapName: dataMapName as 'design_data' | 'document_data',
			dataQueryName,
			inflatedDataItem,
			masterPageData,
			resolvedData,
			pageCompId,
			inflatedDataAcc,
			getRemoteAndMasterPageStructure,
		})
	}
}

export const inflateRefData = (
	inflatedStructure: InflatedByRefStructure,
	{ structure: resolvedStructure, data: resolvedData }: ResolvedRef,
	masterPageData: Data | RefData,
	refId: string,
	pageCompId: string,
	getRemoteAndMasterPageStructure: () => Structure,
	presets: Array<ResolvedPresetsRefArray['values']>,
	clientSpecMap: ViewerClientSpecMap,
	mergedData: Data
): RefData => {
	/*
	 * TODO refactor this whole thing
	 *
	 * currently this code works in a weird way. it runs over the structure and inflate data items that are
	 * related to the structure items. then it runs on the rest of the items and inflate them.
	 *
	 * instead, the code should just run on the entire data and inflate it all since none of the items are expected
	 * to be present in the target document.
	 *
	 * In addition a lot of parts in the code rely on side-effects/mutating an input accumulator object instead of returning
	 * resolved data (not an issue but make it a lot hard to reason about the code)
	 * */
	// inflate data items referenced in the structure
	const inflatedData = _.reduce(
		inflatedStructure,
		(inflatedDataAcc, comp) => {
			if (comp.componentType === 'RemoteRefDeadComp') {
				return inflatedDataAcc
			}
			DATA_QUERIES_TO_INFLATE.forEach((dataQueryName) => {
				const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
				const originalDataItem = getOriginalDataItem(dataQueryName, comp, resolvedStructure, resolvedData)
				if (originalDataItem) {
					if (shouldInflateRefArray(originalDataItem, dataMapName)) {
						inflateRefArray({
							refArray: (originalDataItem as unknown) as UnresolvedRefArray,
							dataQuery: comp[dataQueryName],
							mapName: dataMapName as 'layout_data' | 'theme_data',
							inflatedDataAcc,
							presets,
							mergedData,
						})
					} else {
						inflateSingle({
							originalDataItem,
							dataQueryName,
							comp,
							resolvedStructure,
							refId,
							inflatedDataAcc,
							dataMapName,
							masterPageData,
							resolvedData,
							pageCompId,
							getRemoteAndMasterPageStructure,
							clientSpecMap,
						})
					}
				}
			})
			return inflatedDataAcc
		},
		getEmptyDataMaps()
	)

	// inflate unreferenced data items (i.e inner ref overrides)

	const inflateDataItemId = (originalItemId: string, queryName: string): string => {
		const [, idBase, suffix] = new RegExp(`(.+)(-${queryName}.*)`, 'g').exec(originalItemId) || [
			originalItemId,
			originalItemId,
			'',
		]
		return getUniqueId(refId, `${idBase}${suffix}`)
	}

	const inflateDataItem = (itemId: string, item: any, dataMapName: string): any => {
		const id = inflateDataItemId(itemId, MAP_NAME_TO_QUERY[dataMapName])

		const changes: any = {}
		if (dataMapName === DATA_MAPS.CONNECTIONS && item.type === 'ConnectionList') {
			changes.items = item.items.map((connectionItem: ControllerConnectionItem) =>
				fixInflatedConnectionItem(connectionItem, resolvedStructure, refId)
			)
		}

		return {
			...item,
			...changes,
			id,
		}
	}

	_.values(DATA_MAPS).forEach((dataMapName) => {
		const inflatedItemIds = Object.keys(inflatedData[dataMapName])
		const uninflatedData = _.omit(resolvedData[dataMapName], inflatedItemIds) as {
			[itemId: string]: BaseDataItem<string>
		}
		Object.entries(uninflatedData).forEach(([itemId, item]) => {
			const inflatedItem = inflateDataItem(itemId, item, dataMapName)
			inflatedData[dataMapName][inflatedItem.id] = inflatedItem
		})
	})

	return inflatedData
}
